/**
 * Generic SOAP API client with retry and cancellation support
 *
 * Provides a robust client for SOAP API calls with:
 * - Automatic retry with exponential backoff (via axios-retry)
 * - Request cancellation support
 * - Enhanced error handling
 * - XML parsing and validation
 *
 * @example
 * const client = new SoapClient({
 *   endpoint: '/api/soap',
 *   namespace: 'http://example.com/schema',
 * });
 *
 * const response = await client.call({
 *   action: 'GetData',
 *   body: xmlRequestBody,
 * });
 */

import axios, { type AxiosError, type AxiosInstance } from 'axios';
import axiosRetry, { isNetworkOrIdempotentRequestError } from 'axios-retry';
import { XMLParser } from 'fast-xml-parser';
import { z } from 'zod';
import { PlayerApiError, SoapFaultError, NetworkError, ValidationError } from '@/types/errors';
import { isSoapEnvelope } from '@/types/guards';

/**
 * Retry configuration for axios-retry
 */
export interface RetryConfig {
  retries?: number;
  retryDelay?: (retryCount: number, error: AxiosError) => number;
}

/**
 * SOAP client configuration
 */
export interface SoapClientConfig {
  endpoint: string;
  namespace: string;
  timeout?: number;
  retryConfig?: RetryConfig;
}

/**
 * SOAP call configuration
 */
export interface SoapCallConfig<TResponse = unknown> {
  action: string;
  body: string;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  schema?: z.ZodSchema<TResponse>;
}

/**
 * Generic SOAP API client with retry and cancellation support
 *
 * Uses axios-retry for transparent retry logic with exponential backoff.
 * Handles SOAP API communication with error handling and XML parsing.
 */
export class SoapClient {
  private readonly config: Required<Omit<SoapClientConfig, 'retryConfig'>>;
  private readonly axiosInstance: AxiosInstance;
  private readonly xmlParser: XMLParser;

  constructor(config: SoapClientConfig) {
    this.config = {
      timeout: 30000,
      endpoint: config.endpoint,
      namespace: config.namespace,
    };

    // Create dedicated axios instance
    this.axiosInstance = axios.create({
      timeout: this.config.timeout,
    });

    // Configure axios-retry for automatic retries
    axiosRetry(this.axiosInstance, {
      retries: config.retryConfig?.retries ?? 3,
      retryDelay: config.retryConfig?.retryDelay ?? axiosRetry.exponentialDelay,
      retryCondition: (error) => {
        // Retry on network errors, timeouts, and 5xx server errors
        if (isNetworkOrIdempotentRequestError(error)) {
          return true;
        }
        // Also retry on 5xx server errors
        const status = error.response?.status ?? 0;
        return status >= 500 && status < 600;
      },
    });

    this.xmlParser = new XMLParser({
      ignoreAttributes: false,
      attributeNamePrefix: '@_',
      removeNSPrefix: true,
    });
  }

  /**
   * Execute SOAP API call with automatic retry logic
   *
   * Retry is handled transparently by axios-retry interceptor.
   * Optional Zod schema validation provides runtime type checking.
   *
   * @param config - SOAP call configuration
   * @returns Parsed response with raw XML
   * @throws PlayerApiError, SoapFaultError, NetworkError, or ValidationError
   *
   * @example Basic usage
   * const response = await client.call({
   *   action: 'GetProductList',
   *   body: xmlRequest,
   * });
   *
   * @example With Zod validation
   * import { GetProductListResultSchema } from '@/types/schemas';
   *
   * const response = await client.call({
   *   action: 'GetProductList',
   *   body: xmlRequest,
   *   schema: GetProductListResultSchema,
   * });
   * // response.parsed is now validated and typed correctly
   *
   * @example With cancellation
   * const controller = new AbortController();
   * const response = await client.call({
   *   action: 'GetProductList',
   *   body: xmlRequest,
   *   signal: controller.signal,
   * });
   */
  async call<TResponse = unknown>(
    config: SoapCallConfig<TResponse>
  ): Promise<{ raw: string; parsed: TResponse }> {
    try {
      const response = await this.axiosInstance.post(this.config.endpoint, config.body, {
        headers: {
          'Content-Type': 'text/xml;charset=UTF-8',
          SOAPAction: `"${this.config.namespace}/${config.action}"`,
          ...config.headers,
        },
        signal: config.signal,
      });

      // Parse XML response
      const parsed = this.xmlParser.parse(response.data);

      // Validate response structure
      if (!isSoapEnvelope(parsed)) {
        throw new PlayerApiError(
          'Invalid SOAP response structure',
          'INVALID_RESPONSE',
          this.config.endpoint,
          response.data
        );
      }

      // Check for SOAP Fault
      if (this.isSoapFault(parsed)) {
        throw this.extractSoapFault(parsed);
      }

      // Optional: Validate with Zod schema
      if (config.schema) {
        const validation = config.schema.safeParse(parsed);
        if (!validation.success) {
          throw new ValidationError(
            'Invalid response structure from API',
            validation.error.issues.map((issue) => ({
              field: issue.path.join('.'),
              message: issue.message,
            }))
          );
        }
        return {
          raw: response.data,
          parsed: validation.data,
        };
      }

      return {
        raw: response.data,
        parsed: parsed as TResponse,
      };
    } catch (error) {
      throw this.handleError(error, config.action);
    }
  }

  /**
   * Check if response is SOAP Fault
   *
   * @private
   */
  private isSoapFault(parsed: unknown): boolean {
    if (!isSoapEnvelope(parsed)) return false;
    return !!parsed.Envelope.Body.Fault;
  }

  /**
   * Extract SOAP Fault details
   *
   * @private
   */
  private extractSoapFault(parsed: { Envelope: { Body: { Fault?: unknown } } }): SoapFaultError {
    const fault = parsed.Envelope.Body.Fault as {
      faultcode?: string;
      faultstring?: string;
      detail?: unknown;
    };

    return new SoapFaultError(
      fault.faultstring || 'SOAP Fault occurred',
      fault.faultcode || 'UNKNOWN',
      fault.faultstring || '',
      fault.detail
    );
  }

  /**
   * Handle and normalize errors
   *
   * @private
   */
  private handleError(error: unknown, endpoint?: string): PlayerApiError {
    // Already a PlayerApiError
    if (error instanceof PlayerApiError) {
      return error;
    }

    // Axios error
    if (axios.isAxiosError(error)) {
      return this.handleAxiosError(error, endpoint);
    }

    // Generic error
    if (error instanceof Error) {
      return new PlayerApiError(error.message, 'UNKNOWN_ERROR', endpoint);
    }

    return new PlayerApiError('Unknown error occurred', 'UNKNOWN', endpoint);
  }

  /**
   * Handle axios-specific errors
   *
   * @private
   */
  private handleAxiosError(error: AxiosError, endpoint?: string): PlayerApiError {
    // Request cancellation
    if (axios.isCancel(error)) {
      return new PlayerApiError('Request was cancelled', 'REQUEST_CANCELLED', endpoint);
    }

    // Timeout
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      return new NetworkError(`Request timeout after ${this.config.timeout}ms`, error, true);
    }

    // Network errors
    if (
      error.code === 'ENOTFOUND' ||
      error.code === 'ECONNREFUSED' ||
      error.code === 'ECONNRESET'
    ) {
      return new NetworkError(`Network error: ${error.message}`, error, false);
    }

    // HTTP errors
    if (error.response) {
      return new PlayerApiError(
        `HTTP ${error.response.status}: ${error.message}`,
        error.code || 'HTTP_ERROR',
        endpoint,
        error.response.data as string,
        error.response.status
      );
    }

    return new PlayerApiError(error.message, error.code || 'AXIOS_ERROR', endpoint);
  }
}
